/**
 * SCQ order page — custom elements + shared behaviour.
 *
 *  <scq-package-selector>  tier selector (rendered twice; instances sync via the window
 *                          CustomEvent `scq:package-change` { detail: { qty } })
 *  <scq-order-header>      header bar, fixed after N px of scroll (desktop only)
 *  <scq-sticky-cta>        mobile #call-btn, shown once the hero selector has scrolled past
 *  <scq-gallery>           main + thumbs Swiper
 *  <scq-slider>            before/after Swiper with pagination
 *
 *  Delegated: any `.scq-js-scroll-to[data-target]` smooth-scrolls to its target (siblings use
 *  `<a class="scq-btn scq-btn--pill scq-js-scroll-to" data-target="#select_pakge">`).
 *
 * Storage keys / event payloads mirror the headless order4 page (see docs spec).
 */
(function () {
  if (window.ScqOrder) return;

  var S = window.ScqStorage;
  var KEYS = (S && S.KEYS) || {
    systemQty: 'order4_system_qty',
    mainBundle: 'mainBundleProducts',
    additional: 'additionalProducts',
    orderBump: 'recommendedPopulateOrderBump',
  };
  var EVENT = 'scq:package-change';
  var SCROLL_OFFSET = 70;

  function get(key, kind) {
    return S ? S.safeGet(key, kind) : null;
  }
  function set(key, value, kind) {
    return S ? S.safeSet(key, value, kind) : false;
  }
  function track(event, props) {
    if (S && S.track) S.track(event, props);
  }

  /** Integer cents → "$59.67" / "$179" (no trailing .00, always two decimals otherwise). */
  function formatCents(cents) {
    cents = Math.round(cents);
    var neg = cents < 0 ? '-' : '';
    cents = Math.abs(cents);
    if (cents % 100 === 0) return neg + '$' + (cents / 100).toLocaleString('en-US');
    return neg + '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /**
   * POST a `/cart/add.js` payload, rejecting with Shopify's own error description.
   *
   * A failure here says nothing about whether the cart was modified, and the status code is not a
   * reliable proxy. A 422 is a genuine application reject (the atomic multi-item write never
   * commits), but a 502/504 can equally follow an add the origin *did* commit before the gateway
   * gave up. Never branch on the status: ask the cart instead — see `cartHasAnyReq`.
   *
   * `err.status` is carried for telemetry only (0 for a network-level failure).
   */
  function addToCart(url, items) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    }).then(
      function (res) {
        if (res.ok) return res;
        return res
          .json()
          .catch(function () {
            return {};
          })
          .then(function (data) {
            var err = new Error(data.description || data.message || 'Could not add to cart');
            err.status = res.status;
            throw err;
          });
      },
      function (err) {
        err.status = 0;
        throw err;
      }
    );
  }

  /**
   * Did any add stamped with one of `reqIds` reach the cart? The System line needs exactly-once
   * semantics, and only an observed cart can establish whether it committed.
   *
   * Resolves to one of three states — the caller must handle all three:
   *   'landed'  — a line carries one of these `_scq_req`s. The whole payload committed
   *               (`/cart/add.js` is atomic), so the gifts are in too. Go to checkout; never
   *               re-post.
   *   'absent'  — the cart was read cleanly and holds no such line. Provably nothing committed,
   *               so a re-post is safe.
   *   'unknown' — the read itself failed. We are blind, so we must assume the add may have landed
   *               and refuse to re-post. Surfacing an error beats risking a second paid line.
   */
  function cartHasAnyReq(cartUrl, reqIds) {
    return fetch(cartUrl, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('cart read failed');
        return res.json();
      })
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        var found = items.some(function (item) {
          return item && item.properties && reqIds.indexOf(item.properties._scq_req) !== -1;
        });
        return found ? 'landed' : 'absent';
      })
      .catch(function () {
        return 'unknown';
      });
  }

  /** Unique per click, so a failed add can be attributed to *this* attempt and nothing else. */
  function newReqId() {
    return 'r' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }

  function shipsBy() {
    var d = new Date();
    d.setDate(d.getDate() + 2);
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  }

  function primarySkinType() {
    try {
      return (S && S.readQuizState && S.readQuizState().primary) || '';
    } catch (e) {
      return '';
    }
  }

  /** Shared selection: persist + broadcast. Both selectors listen and re-render. */
  function selectQty(qty, tier, ctx) {
    set(KEYS.systemQty, String(qty), 'session');
    if (tier) {
      // Headless CartItem shape { id, name, price, comparePrice?, image, productId } — analytics
      // parity only; Shopify checkout reads the real cart. No priceKey: that field is a headless
      // price-table key and nothing here maps to one.
      var numericId = parseInt(ctx.variantId, 10);
      var item = {
        id: isNaN(numericId) ? ctx.variantId : numericId,
        name: ctx.productName + ' ×' + qty,
        price: tier.sale / 100,
        comparePrice: tier.regular / 100,
        image: ctx.image || '',
        productId: isNaN(numericId) ? ctx.variantId : numericId,
      };
      set(KEYS.mainBundle, JSON.stringify([item]), 'local');
      set(KEYS.additional, '[]', 'local');
      set(KEYS.orderBump, 'null', 'local');
    }
    window.dispatchEvent(new CustomEvent(EVENT, { detail: { qty: qty } }));
  }

  /* ------------------------------------------------------------------ selector */
  if (!customElements.get('scq-package-selector')) {
    customElements.define(
      'scq-package-selector',
      class ScqPackageSelector extends HTMLElement {
        connectedCallback() {
          var el = this;
          this.ctx = {
            variantId: (this.dataset.variantId || '').trim(),
            productName: this.dataset.productName || 'Total Skin Renewal System',
            image: this.dataset.image || '',
            checkoutUrl: this.dataset.checkoutUrl || '/checkout',
            cartAddUrl: (window.routes && window.routes.cart_add_url) || this.dataset.cartAddUrl || '/cart/add',
            cartUrl: (window.routes && window.routes.cart_url) || this.dataset.cartUrl || '/cart',
            // Free Gift E-books, resolved from handles by scq-package-selector.liquid. Empty when
            // none of the three products resolved — the funnel still sells without them.
            giftVariantIds: (this.dataset.giftVariants || '')
              .split(',')
              .map(function (id) {
                return parseInt(id, 10);
              })
              // `id > 0` also drops NaN. De-duplicate so the same handle entered twice can't
              // ship the customer two copies of one e-book.
              .filter(function (id, i, all) {
                return id > 0 && all.indexOf(id) === i;
              }),
          };
          this.tiers = {};
          // `_scq_req` tokens from clicks whose outcome we could not read back off the cart. Non-
          // empty means the next click must check the cart before posting, or it may add a second
          // paid line on top of one that quietly committed. Cleared by that check — see `buy()`.
          this.pendingReqIds = [];
          this.options = Array.prototype.slice.call(this.querySelectorAll('.scq-selector__option'));
          this.options.forEach(function (opt) {
            var qty = parseInt(opt.dataset.qty, 10);
            el.tiers[qty] = {
              qty: qty,
              sale: parseInt(opt.dataset.sale, 10) || 0,
              regular: parseInt(opt.dataset.regular, 10) || 0,
              saleText: opt.dataset.saleText || '',
              regularText: opt.dataset.regularText || '',
              el: opt,
            };
            var per = opt.querySelector('[data-per-system]');
            if (per && qty > 0) per.textContent = formatCents(el.tiers[qty].sale / qty);
          });

          this.cta = this.querySelector('[data-cta]');
          this.error = this.querySelector('.scq-selector__error');
          this.querySelectorAll('[data-ships-by], [data-scq-ships-by]').forEach(function (n) {
            n.textContent = shipsBy();
          });

          var initial = parseInt(get(KEYS.systemQty, 'session'), 10);
          if (!this.tiers[initial]) initial = parseInt(this.dataset.defaultQty, 10) || 2;
          if (!this.tiers[initial]) initial = parseInt(Object.keys(this.tiers)[0], 10);
          this.render(initial);
          // Persist the restored/default selection (headless does the same on mount).
          set(KEYS.systemQty, String(initial), 'session');

          this.onChange = function (e) {
            var input = e.target.closest('.scq-selector__input');
            if (!input) return;
            var qty = parseInt(input.value, 10);
            selectQty(qty, el.tiers[qty], el.ctx);
          };
          this.addEventListener('change', this.onChange);

          this.onExternal = function (e) {
            if (e.detail && e.detail.qty !== el.qty) el.render(e.detail.qty);
          };
          window.addEventListener(EVENT, this.onExternal);

          if (this.cta) {
            this.onBuy = function () {
              el.buy();
            };
            this.cta.addEventListener('click', this.onBuy);
          }

          if (this.hasAttribute('data-track-page-view') && !window.__scqOrderViewed) {
            window.__scqOrderViewed = true;
            track('page_view', { page: 'scq-order' });
          }
        }

        disconnectedCallback() {
          window.removeEventListener(EVENT, this.onExternal);
          this.removeEventListener('change', this.onChange);
          if (this.cta) this.cta.removeEventListener('click', this.onBuy);
        }

        render(qty) {
          var tier = this.tiers[qty];
          if (!tier) return;
          this.qty = qty;
          this.options.forEach(function (opt) {
            var active = parseInt(opt.dataset.qty, 10) === qty;
            opt.classList.toggle('is-active', active);
            var input = opt.querySelector('.scq-selector__input');
            if (input) input.checked = active;
          });
          var total = this.querySelector('[data-total]');
          var regular = this.querySelector('[data-regular-total]');
          var savings = this.querySelector('[data-savings]');
          if (total) total.textContent = tier.saleText || formatCents(tier.sale);
          if (regular) regular.textContent = tier.regularText || formatCents(tier.regular);
          if (savings) savings.textContent = formatCents(tier.regular - tier.sale);
        }

        setBusy(busy) {
          if (!this.cta) return;
          this.cta.disabled = busy;
          this.cta.classList.toggle('is-loading', busy);
          this.cta.setAttribute('aria-busy', busy ? 'true' : 'false');
        }

        showError(msg) {
          if (!this.error) return;
          this.error.textContent = msg;
          this.error.hidden = false;
        }

        buy() {
          var el = this;
          var qty = this.qty;
          var tier = this.tiers[qty];
          if (!tier || !this.ctx.variantId || this.busy) return;
          this.busy = true;
          if (this.error) this.error.hidden = true;
          this.setBusy(true);

          selectQty(qty, tier, this.ctx);
          track('order_cta_click', { qty: qty, page: 'order' });

          var url = this.ctx.cartAddUrl;
          if (url.indexOf('.js') === -1) url += '.js';
          var cartUrl = this.ctx.cartUrl;
          if (cartUrl.indexOf('.js') === -1) cartUrl += '.js';

          // `_scq_tier` marks the line for the pricing Function (DEV-1296); `_scq_gift` marks the
          // $0 e-book lines for reporting only. `_scq_req` is this click's idempotency token, the
          // only thing that lets a failed add be attributed to *this* attempt rather than to a
          // line the cart already held. All three are `_`-prefixed, so none is shown to the
          // customer in checkout or on the order (staff do see them on the order in admin).
          //
          // Side effect worth knowing: a unique `_scq_req` stops Shopify merging two otherwise
          // identical System lines. The pricing Function caps per line, so its 3-unit cap stays
          // the real boundary either way — see DEV-1296.
          var reqId = newReqId();
          var systemLine = {
            id: Number(this.ctx.variantId),
            quantity: qty,
            properties: { _scq_tier: String(qty), _scq_skin_type: primarySkinType(), _scq_req: reqId },
          };
          var giftLines = this.ctx.giftVariantIds.map(function (id) {
            return { id: id, quantity: 1, properties: { _scq_gift: 'true' } };
          });

          /**
           * Post `items`, and on failure ask the cart what actually happened instead of guessing
           * from the status code. Resolves once the System line is known to be in the cart; on
           * rejection `err.cartState` tells the caller whether a re-post is safe.
           */
          function postAndResolve(items) {
            return addToCart(url, items).catch(function (err) {
              return cartHasAnyReq(cartUrl, [reqId]).then(function (state) {
                if (state === 'landed') return;
                // Blind: a later click on this page must pre-flight before posting again.
                if (state === 'unknown') el.pendingReqIds.push(reqId);
                err.cartState = state;
                throw err;
              });
            });
          }

          // Only needed once an earlier click has ended blind — otherwise there is nothing this
          // page could have committed without knowing about it, and we skip the extra round trip.
          //
          // One-shot: the tokens are dropped as soon as the check resolves, whichever way it goes.
          // They guard the customer's immediate re-click, not the page for the rest of its life —
          // keeping them would mean a customer who back-navigates from checkout to change tier
          // gets pre-flighted onto the old line every time, with no way to pick a different one.
          var preflight = this.pendingReqIds.length
            ? cartHasAnyReq(cartUrl, this.pendingReqIds).then(function (state) {
                el.pendingReqIds = [];
                return state;
              })
            : Promise.resolve('absent');

          preflight
            .then(function (state) {
              // An earlier ambiguous click did commit after all — go to checkout on that line
              // rather than adding a second one.
              //
              // If the customer changed tier in between, that line is the *old* tier. Accepted:
              // reaching here needs an add and a cart read to fail on one click and the network to
              // then recover, and Shopify's checkout lists the line items and total before payment,
              // so a wrong tier is visible and recoverable. A second paid line would not be.
              if (state === 'landed') return;
              if (state === 'unknown') {
                throw new Error('We could not confirm your cart. Please open your cart to check before trying again.');
              }
              return postAndResolve([systemLine].concat(giftLines)).catch(function (err) {
                // /cart/add.js is all-or-nothing, so one e-book going out of stock would otherwise
                // reject the System line with it. A free gift must never cost us the sale: re-post
                // without the gifts and let checkout proceed.
                //
                // Gated on a *provably* untouched cart. Anything else — including a 5xx the origin
                // may have committed before the gateway gave up — leaves a second System line on
                // the table, which is real money. Better to surface the error than to double-charge.
                if (err.cartState !== 'absent' || !giftLines.length) throw err;
                return postAndResolve([systemLine]).then(function () {
                  // Reached only if the System alone succeeds, so the gifts really were at fault —
                  // a sold-out System would have failed here too and fallen through to the catch.
                  console.warn('[scq-order] free e-books could not be added; continuing without them', err);
                  track('gift_add_failed', { qty: qty, status: err.status, reason: err.message || 'unknown' });
                });
              });
            })
            .then(function () {
              window.location.href = el.ctx.checkoutUrl;
            })
            .catch(function (err) {
              console.warn('[scq-order] add to cart failed', err);
              el.showError((err && err.message) || 'Something went wrong. Please try again.');
              el.busy = false;
              el.setBusy(false);
            });
        }
      }
    );
  }

  /* -------------------------------------------------------------------- header */
  if (!customElements.get('scq-order-header')) {
    customElements.define(
      'scq-order-header',
      class ScqOrderHeader extends HTMLElement {
        connectedCallback() {
          var el = this;
          var threshold = parseInt(this.dataset.fixedAfter, 10);
          if (isNaN(threshold)) threshold = 110;
          this.onScroll = function () {
            el.classList.toggle('is-fixed', window.innerWidth > 767 && window.scrollY > threshold);
          };
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onScroll);
          this.onScroll();
        }
        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
        }
      }
    );
  }

  /* ---------------------------------------------------------------- sticky CTA */
  if (!customElements.get('scq-sticky-cta')) {
    customElements.define(
      'scq-sticky-cta',
      class ScqStickyCta extends HTMLElement {
        connectedCallback() {
          var el = this;
          var selectors = (this.dataset.watch || '#select_pakge,#select_pakge2').split(',');
          this.targets = selectors
            .map(function (sel) {
              return document.querySelector(sel.trim());
            })
            .filter(Boolean);
          this.hidden = true;
          if (!this.targets.length) return;
          this.hero = this.targets[0];
          this.update = this.update.bind(this);
          this.scheduled = false;
          this.onScroll = function () {
            if (el.scheduled) return;
            el.scheduled = true;
            window.requestAnimationFrame(function () {
              el.scheduled = false;
              el.update();
            });
          };
          // Geometry is re-derived from live bounding rects on every scroll/resize, so a jump
          // (Home key, anchor, restored scroll) can never leave a stale "hero passed" latch behind.
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onScroll);
          this.update();
        }
        update() {
          var vh = window.innerHeight || document.documentElement.clientHeight;
          var heroRect = this.hero.getBoundingClientRect();
          var heroPassed = heroRect.bottom < 0;
          var anySelectorVisible = this.targets.some(function (t) {
            var r = t.getBoundingClientRect();
            return r.bottom > 0 && r.top < vh;
          });
          this.hidden = !(heroPassed && !anySelectorVisible);
        }
        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
        }
      }
    );
  }

  /* -------------------------------------------------------------------- swiper */
  function whenSwiper(cb, tries) {
    if (window.Swiper) return cb(window.Swiper);
    if (tries <= 0) return;
    setTimeout(function () {
      whenSwiper(cb, tries - 1);
    }, 100);
  }

  if (!customElements.get('scq-gallery')) {
    customElements.define(
      'scq-gallery',
      class ScqGallery extends HTMLElement {
        connectedCallback() {
          var el = this;
          whenSwiper(function (Swiper) {
            if (el.main) return;
            var thumbEl = el.querySelector('.scq-gallery__thumbs');
            var mainEl = el.querySelector('.scq-gallery__main');
            if (thumbEl) {
              el.thumbs = new Swiper(thumbEl, {
                spaceBetween: 7,
                slidesPerView: 5,
                watchSlidesProgress: true,
                watchOverflow: true,
                freeMode: true,
                breakpoints: { 0: { slidesPerView: 5, spaceBetween: 8 }, 768: { slidesPerView: 5, spaceBetween: 7 } },
              });
            }
            if (mainEl) {
              el.main = new Swiper(mainEl, {
                loop: true,
                speed: 600,
                spaceBetween: 0,
                navigation: {
                  nextEl: el.querySelector('.scq-gallery__arrow--next'),
                  prevEl: el.querySelector('.scq-gallery__arrow--prev'),
                },
                thumbs: el.thumbs ? { swiper: el.thumbs } : undefined,
              });
            }
          }, 50);
        }
        disconnectedCallback() {
          if (this.main && this.main.destroy) this.main.destroy(true, true);
          if (this.thumbs && this.thumbs.destroy) this.thumbs.destroy(true, true);
          this.main = null;
          this.thumbs = null;
        }
      }
    );
  }

  if (!customElements.get('scq-slider')) {
    customElements.define(
      'scq-slider',
      class ScqSlider extends HTMLElement {
        connectedCallback() {
          var el = this;
          whenSwiper(function (Swiper) {
            if (el.swiper) return;
            el.swiper = new Swiper(el, {
              spaceBetween: 15,
              slidesPerView: 1.5,
              loop: true,
              pagination: { el: el.querySelector('.swiper-pagination'), clickable: true },
              breakpoints: {
                0: { slidesPerView: 1.3, spaceBetween: 15 },
                768: { slidesPerView: 2 },
                1021: { slidesPerView: 2, spaceBetween: 15 },
              },
            });
          }, 50);
        }
        disconnectedCallback() {
          if (this.swiper && this.swiper.destroy) this.swiper.destroy(true, true);
          this.swiper = null;
        }
      }
    );
  }

  /* -------------------------------------------------------- delegated scroll-to */
  document.addEventListener('click', function (e) {
    var link = e.target.closest('.scq-js-scroll-to[data-target]');
    if (!link) return;
    var node;
    try {
      node = document.querySelector(link.dataset.target);
    } catch (err) {
      node = null;
    }
    if (!node) return;
    e.preventDefault();
    window.scrollTo({ top: node.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET, behavior: 'smooth' });
  });

  /* Fill every "Ships by" date placeholder on the page (content-section CTAs render an empty
     <span data-scq-ships-by></span> after the "Ships by" prefix; selectors fill their own too). */
  function fillShipsBy() {
    var date = shipsBy();
    document.querySelectorAll('[data-scq-ships-by], [data-ships-by]').forEach(function (n) {
      n.textContent = date;
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fillShipsBy);
  } else {
    fillShipsBy();
  }

  window.ScqOrder = { EVENT: EVENT, selectQty: selectQty, formatCents: formatCents, shipsBy: shipsBy, fillShipsBy: fillShipsBy };
})();
